import { BulkDashboardTab, setActiveTab } from '@common-reducers/CampaignReducer';
import { getCampaignDueDate, getCampaignSelectedAudenicesAmount, getIsCampaginMessageExist } from '@common-reducers/CampaignSelectors';
import { useAnalyticsService } from '@common-reducers/hooks/use-analytics-service';
import { AnalyticsService } from '@common-services/analytics-service';
import { useAppDispatch, useAppSelector } from '@common-reducers/hooks/store.hook';
import { Users, MessageSquare, Calendar, Rocket, Check } from 'lucide-react';
import React, { useMemo } from 'react';
import { cn } from '@/lib/utils';
import { useTranslation } from 'react-i18next';
import { namespace } from '@common/config/constants';
import { useIsMobile } from '@common/hooks/use-is-mobile';


export default function LaunchCampaignFlowTabs() {
    const { t } = useTranslation(namespace);
    const dispatch = useAppDispatch()
    const isMobile = useIsMobile();
    const campaignSelectedAudenicesAmount = useAppSelector((state) => getCampaignSelectedAudenicesAmount(state));
    const campaignDueDate = useAppSelector((state) => getCampaignDueDate(state));
    const isCampaginMessageExist = useAppSelector((state) => getIsCampaginMessageExist(state));
    const activeTab = useAppSelector((state) => state.CampaignReducer.activeTab);
    const analyticsService: AnalyticsService = useAnalyticsService();

    const tabsData = useMemo(() => [
        {
            label: t('campaign.selectTargetAudience'),
            value: "audiences",
            icon: Users,
        },
        {
            label: t('campaign.writeYourMessage'),
            value: "messageAndFiles",
            icon: MessageSquare,
        },
        {
            label: t('campaign.setLaunchTime'),
            value: "timeAndDate",
            icon: Calendar,
        },
        {
            label: t('campaign.launchCampaign'),
            value: "finish",
            icon: Rocket,
        },
    ], [t]);



    const getActiveIndex = () => {

        let activeTabIndex = 0;
        if (activeTab === "audiences") {
            activeTabIndex = 0;
        }
        else if (activeTab === "messageAndFiles") {
            activeTabIndex = 1;
        }
        else if (activeTab === "timeAndDate") {
            activeTabIndex = 2;
        }
        else if (activeTab === "finish") {
            activeTabIndex = 3;
        }

        return activeTabIndex;
    }

    const isStepClickable = (stepValue: string) => {
        let isClickable = false;
        if (stepValue === "audiences") {
            isClickable = true; // First step, no previous condition.
        }
        else if (stepValue === "messageAndFiles") {
            // Now also checks if the first step is 'clickable'
            isClickable = campaignSelectedAudenicesAmount > 0;
        }
        else if (stepValue === "timeAndDate") {
            // Checks if the first and second steps are 'clickable'
            isClickable = campaignSelectedAudenicesAmount > 0 && !!isCampaginMessageExist;
        }
        else if (stepValue === "finish") {
            // Checks if the first, second, and third steps are 'clickable'
            isClickable = campaignSelectedAudenicesAmount > 0 && !!isCampaginMessageExist && !!campaignDueDate;
        }
        return isClickable;
    }


    const handleBulkCampaignTabChange = (newValue: string) => {
        analyticsService.event('bulk_dashboard_tab_change', {
            category: 'bulk',
            action: newValue
        });

        dispatch(setActiveTab(newValue as BulkDashboardTab));
    }

    const activeIndex = getActiveIndex();

    return (
        <div className={cn("px-6 py-8 w-full", isMobile && "px-3 py-5")}>
            <div className="max-w-5xl mx-auto w-full">
                <div className="flex items-center justify-between relative">
                    {/* Connection Line - Background */}
                    <div className="absolute top-6 left-0 right-0 h-0.5 bg-slate-200 dark:bg-slate-700 -z-10">
                        {/* Progress Line */}
                        <div 
                            className="h-full transition-all duration-500"
                            style={{ 
                                width: `${((activeIndex) / (tabsData.length - 1)) * 100}%`,
                                background: "linear-gradient(66.67deg, #259ae9 0%, #6dd1f1 100%)"
                            }}
                        ></div>
                    </div>

                    {tabsData.map((step, index) => {
                        const Icon = step.icon;
                        const isActive = index === activeIndex;
                        const isCompleted = index < activeIndex;
                        const isClickable = isStepClickable(step.value);

                        return (
                            <div 
                                key={step.value}
                                className="flex flex-col items-center flex-1 cursor-pointer"
                                onClick={() => isClickable && handleBulkCampaignTabChange(step.value)}
                            >
                                {/* Icon Circle */}
                                <div 
                                    className={cn(
                                        "w-12 h-12 rounded-full flex items-center justify-center mb-3 transition-all",
                                        isActive 
                                            ? "text-white shadow-lg scale-110" 
                                            : isCompleted
                                            ? "text-white"
                                            : "bg-white dark:bg-slate-800 text-slate-400 dark:text-slate-500 border-2 border-slate-200 dark:border-slate-700"
                                    )}
                                    style={isActive || isCompleted ? {
                                        background: "linear-gradient(66.67deg, #259ae9 0%, #6dd1f1 100%)"
                                    } : undefined}
                                >
                                    {isCompleted && index !== tabsData.length - 1 ? (
                                        <Check className="w-6 h-6" />
                                    ) : (
                                        <Icon className="w-6 h-6" />
                                    )}
                                </div>

                                {/* Label - Top aligned for consistent first-line alignment */}
                                <p 
                                    className={cn(
                                        "text-sm font-medium text-center max-w-[140px] leading-tight min-h-[2.5rem]",
                                        isActive 
                                            ? ""  
                                            : isCompleted 
                                            ? "text-slate-600 dark:text-slate-400" 
                                            : "text-slate-400 dark:text-slate-500"
                                    )}
                                    style={isActive ? { color: "#259ae9" } : undefined}
                                >
                                    {step.label}
                                </p>
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    )
}